import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { LogIn, Mail, Lock, Eye, EyeOff, Check, ArrowLeft, Loader2 } from 'lucide-react';
import { authService } from '../../services/authService';
import { Toast } from '../Toast';
import { getAuth, setPersistence, browserLocalPersistence, browserSessionPersistence, User } from 'firebase/auth';
import { GoogleSecureLoginButton } from './GoogleSecureLoginButton';
import { SecureChallengeModal } from './SecureChallengeModal';
import { startPasswordSecureLogin, resolvePasswordDeviceChallenge, resolvePasswordTotpChallenge, resendPasswordDeviceCode } from '../../services/passwordSecureAuthService';
import type { AuthChallenge } from '../../services/secureAuthShared';
import { doc, getDoc } from 'firebase/firestore';
import { db } from '../../lib/firebase';
import { resolvePostLoginLandingPage } from '../../utils/trialDemarrageRedirect';

import logoSvg from '../../assets/focuspips.svg';

// Fonction pour traduire les erreurs Firebase
const getFirebaseErrorMessage = (errorCode: string, t: (key: string) => string): string => {
  switch (errorCode) {
    case 'auth/user-not-found':
      return t('public.login.errors.user_not_found');
    case 'auth/wrong-password':
      return t('public.login.errors.wrong_password');
    case 'auth/invalid-email':
      return t('public.login.errors.invalid_email');
    case 'auth/user-disabled':
      return t('public.login.errors.user_disabled');
    case 'auth/too-many-requests':
      return t('public.login.errors.too_many_requests');
    case 'auth/network-request-failed':
      return t('public.login.errors.network_request_failed');
    case 'auth/invalid-credential':
      return t('public.login.errors.invalid_credential');
    case 'auth/account-exists-with-different-credential':
      return t('public.login.errors.account_exists_diff_credential');
    case 'auth/operation-not-allowed':
      return t('public.login.errors.operation_not_allowed');
    case 'auth/requires-recent-login':
      return t('public.login.errors.requires_recent_login');
    case 'auth/email-already-in-use':
      return t('public.login.errors.email_already_in_use');
    default:
      return t('public.login.errors.default');
  }
};

export function LoginForm() {
  // console.log supprimé

  const { t } = useTranslation();
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    email: '',
    password: ''
  });
  const [showPassword, setShowPassword] = useState(false);
  const [rememberMe, setRememberMe] = useState(() => {
    // Récupérer la préférence depuis le localStorage
    const savedPreference = localStorage.getItem('rememberMe');
    return savedPreference === 'true';
  });
  const [loading, setLoading] = useState(false);
  const [toast, setToast] = useState<{ message: string; type: 'success' | 'error' } | null>(null);
  const [pending, setPending] = useState<{ pendingSessionId: string; challenge: AuthChallenge } | null>(null);

  // Nous gardons seulement le stockage de la préférence "Se souvenir de moi" sans redirection automatique
  // La redirection sera gérée uniquement après une connexion réussie

  /**
   * Post-login commun aux deux chemins (pas de challenge nécessaire / challenge
   * résolu via SecureChallengeModal) : à ce stade user est un vrai utilisateur
   * Firebase authentifié — signInWithCustomToken vient d'avoir lieu.
   */
  const completeLogin = async (user: User) => {
    // Vérifier si le mot de passe n'a pas expiré
    const lastPasswordChange = await authService.getLastPasswordChange(user.uid);
    const MAX_PASSWORD_AGE = 90 * 24 * 60 * 60 * 1000; // 90 jours

    if (lastPasswordChange && Date.now() - lastPasswordChange > MAX_PASSWORD_AGE) {
      navigate('/change-password');
      return;
    }

    // Nettoyer les données sensibles et rediriger
    setFormData({ email: '', password: '' });
    // Récupérer la préférence de page d'arrivée directement depuis Firestore
    let preferredLandingPage = '/dashboard';
    try {
      const userPrefsDoc = await getDoc(doc(db, 'userPreferences', user.uid));
      if (userPrefsDoc.exists()) {
        const userPrefs = userPrefsDoc.data();
        if (userPrefs.defaultLandingPage) {
          preferredLandingPage = userPrefs.defaultLandingPage;
        }
      }
    } catch {
      // En cas d'erreur, utiliser la valeur par défaut
    }
    const landingPage = await resolvePostLoginLandingPage(user.uid, preferredLandingPage);
    navigate(landingPage, { replace: true });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);

    try {
      const sanitizedEmail = formData.email.trim().toLowerCase();
      const sanitizedPassword = formData.password;

      // Validate email format
      if (!sanitizedEmail.match(/^[^\s@]+@[^\s@]+\.[^\s@]+$/)) {
        throw new Error(t('public.login.err_invalid_email_format'));
      }

      // La persistance doit être configurée AVANT tout appel signInWithCustomToken
      // (déclenché soit tout de suite ci-dessous si aucun challenge n'est requis,
      // soit plus tard depuis SecureChallengeModal une fois le challenge résolu) —
      // c'est un réglage de l'instance Auth, pas un paramètre par appel.
      localStorage.setItem('rememberMe', rememberMe.toString());
      await setPersistence(getAuth(), rememberMe ? browserLocalPersistence : browserSessionPersistence);

      // Le mot de passe n'est JAMAIS vérifié par le navigateur : il part
      // directement vers le serveur, qui ne renvoie un customToken qu'une
      // fois l'appareil et/ou la 2FA (si activés) confirmés — voir
      // src/services/passwordSecureAuthService.ts.
      const result = await startPasswordSecureLogin(sanitizedEmail, sanitizedPassword);

      if (result.status === 'challenge') {
        setPending({ pendingSessionId: result.pendingSessionId, challenge: result.challenge });
        setLoading(false);
        return;
      }

      await completeLogin(result.user);
    } catch (error: any) {
      const errorMessage = error.code && error.code.startsWith('auth/')
        ? getFirebaseErrorMessage(error.code, t)
        : error.message || t('public.login.errors.default');
      setToast({ message: errorMessage, type: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const handleChallengeSuccess = async (user: User) => {
    setPending(null);
    try {
      await completeLogin(user);
    } catch (error: any) {
      setToast({ message: error.message || t('public.login.err_challenge_finalize'), type: 'error' });
    }
  };

  const handleChallengeCancel = () => {
    setPending(null);
  };

  return (
    <div className="min-h-screen bg-white text-gray-900 overflow-x-hidden relative" data-theme="light">
      {/* Fond animé avec gradients - Inspiré de LandingPage */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        {/* Gradient orb 1 - Violet */}
        <div 
          className="absolute top-0 -left-20 w-96 h-96 rounded-full opacity-20 blur-3xl"
          style={{
            background: 'radial-gradient(circle, rgba(139, 92, 246, 0.4) 0%, rgba(139, 92, 246, 0) 70%)',
            animation: 'float 20s ease-in-out infinite'
          }}
        />
        {/* Gradient orb 2 - Rose/Orange */}
        <div 
          className="absolute top-40 -right-20 w-96 h-96 rounded-full opacity-20 blur-3xl"
          style={{
            background: 'radial-gradient(circle, rgba(236, 72, 153, 0.3) 0%, rgba(251, 146, 60, 0.3) 50%, rgba(236, 72, 153, 0) 70%)',
            animation: 'float 25s ease-in-out infinite reverse'
          }}
        />
        {/* Gradient orb 3 - Bleu */}
        <div 
          className="absolute bottom-20 left-1/3 w-96 h-96 rounded-full opacity-15 blur-3xl"
          style={{
            background: 'radial-gradient(circle, rgba(59, 130, 246, 0.4) 0%, rgba(59, 130, 246, 0) 70%)',
            animation: 'float 30s ease-in-out infinite'
          }}
        />
        {/* Grille subtile */}
        <div 
          className="absolute inset-0 opacity-5"
          style={{
            backgroundImage: 'linear-gradient(rgba(0, 0, 0, 0.05) 1px, transparent 1px), linear-gradient(90deg, rgba(0, 0, 0, 0.05) 1px, transparent 1px)',
            backgroundSize: '50px 50px'
          }}
        />
      </div>

      <div className="min-h-screen flex relative z-10">
        {/* Left Panel - Login Form */}
        <div className="w-full lg:w-1/2 p-8 sm:p-12 lg:p-16 flex flex-col justify-center relative">
          <div className="max-w-md w-full mx-auto space-y-8">
            <Link 
              to="/" 
              className="flex items-center gap-2 text-gray-600 hover:text-gray-900 transition-colors group"
            >
              <ArrowLeft className="h-4 w-4 transition-transform group-hover:-translate-x-1" />
              <span>{t('public.login.back_home')}</span>
            </Link>

            <div>
              <h1 className="text-4xl sm:text-5xl font-bold mb-3 text-gray-900">
                {t('public.login.title')}
              </h1>
              <p className="text-lg text-gray-600">
                {t('public.login.subtitle')}
              </p>
            </div>

            <form onSubmit={handleSubmit} className="space-y-6">
              <div className="space-y-5">
                <div>
                  <label className="block text-sm font-semibold mb-2 text-gray-900">{t('public.login.email_label')}</label>
                  <div className="relative">
                    <svg className="absolute left-4 top-1/2 -translate-y-1/2 h-5 w-5" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg"><path fillRule="evenodd" clipRule="evenodd" d="M9.94358 3.25H14.0564C15.8942 3.24998 17.3498 3.24997 18.489 3.40314C19.6614 3.56076 20.6104 3.89288 21.3588 4.64124C22.1071 5.38961 22.4392 6.33856 22.5969 7.51098C22.75 8.65019 22.75 10.1058 22.75 11.9436V12.0564C22.75 13.8942 22.75 15.3498 22.5969 16.489C22.4392 17.6614 22.1071 18.6104 21.3588 19.3588C20.6104 20.1071 19.6614 20.4392 18.489 20.5969C17.3498 20.75 15.8942 20.75 14.0564 20.75H9.94359C8.10583 20.75 6.65019 20.75 5.51098 20.5969C4.33856 20.4392 3.38961 20.1071 2.64124 19.3588C1.89288 18.6104 1.56076 17.6614 1.40314 16.489C1.24997 15.3498 1.24998 13.8942 1.25 12.0564V11.9436C1.24998 10.1058 1.24997 8.65019 1.40314 7.51098C1.56076 6.33856 1.89288 5.38961 2.64124 4.64124C3.38961 3.89288 4.33856 3.56076 5.51098 3.40314C6.65019 3.24997 8.10582 3.24998 9.94358 3.25ZM5.71085 4.88976C4.70476 5.02502 4.12511 5.27869 3.7019 5.7019C3.27869 6.12511 3.02502 6.70476 2.88976 7.71085C2.75159 8.73851 2.75 10.0932 2.75 12C2.75 13.9068 2.75159 15.2615 2.88976 16.2892C3.02502 17.2952 3.27869 17.8749 3.7019 18.2981C4.12511 18.7213 4.70476 18.975 5.71085 19.1102C6.73851 19.2484 8.09318 19.25 10 19.25H14C15.9068 19.25 17.2615 19.2484 18.2892 19.1102C19.2952 18.975 19.8749 18.7213 20.2981 18.2981C20.7213 17.8749 20.975 17.2952 21.1102 16.2892C21.2484 15.2615 21.25 13.9068 21.25 12C21.25 10.0932 21.2484 8.73851 21.1102 7.71085C20.975 6.70476 20.7213 6.12511 20.2981 5.7019C19.8749 5.27869 19.2952 5.02502 18.2892 4.88976C17.2615 4.75159 15.9068 4.75 14 4.75H10C8.09318 4.75 6.73851 4.75159 5.71085 4.88976ZM5.42383 7.51986C5.68901 7.20165 6.16193 7.15866 6.48014 7.42383L8.63903 9.22291C9.57199 10.0004 10.2197 10.5384 10.7666 10.8901C11.2959 11.2306 11.6549 11.3449 12 11.3449C12.3451 11.3449 12.7041 11.2306 13.2334 10.8901C13.7803 10.5384 14.428 10.0004 15.361 9.22291L17.5199 7.42383C17.8381 7.15866 18.311 7.20165 18.5762 7.51986C18.8413 7.83807 18.7983 8.31099 18.4801 8.57617L16.2836 10.4066C15.3973 11.1452 14.6789 11.7439 14.0448 12.1517C13.3843 12.5765 12.7411 12.8449 12 12.8449C11.2589 12.8449 10.6157 12.5765 9.95518 12.1517C9.32112 11.7439 8.60272 11.1452 7.71636 10.4066L5.51986 8.57617C5.20165 8.31099 5.15866 7.83807 5.42383 7.51986Z" fill="#9ca3af"/></svg>
                    <input
                      type="email"
                      value={formData.email}
                      onChange={e => setFormData({ ...formData, email: e.target.value })}
                      className="register-input w-full pl-12 pr-4 py-3.5 rounded-xl bg-[#f6f6f6] border-0 focus:ring-2 focus:ring-purple-500/20 transition-all text-gray-900 placeholder-gray-400"
                      style={{ backgroundColor: '#f6f6f6', border: 'none' }}
                      required
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-semibold mb-2 text-gray-900">{t('public.login.password_label')}</label>
                  <div className="relative">
                    <svg className="absolute left-4 top-1/2 -translate-y-1/2 h-5 w-5" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg"><path fillRule="evenodd" clipRule="evenodd" d="M5.25 9.30277V8C5.25 4.27208 8.27208 1.25 12 1.25C15.7279 1.25 18.75 4.27208 18.75 8V9.30277C18.9768 9.31872 19.1906 9.33948 19.3918 9.36652C20.2919 9.48754 21.0497 9.74643 21.6517 10.3483C22.2536 10.9503 22.5125 11.7081 22.6335 12.6082C22.75 13.4752 22.75 14.5775 22.75 15.9451V16.0549C22.75 17.4225 22.75 18.5248 22.6335 19.3918C22.5125 20.2919 22.2536 21.0497 21.6517 21.6516C21.0497 22.2536 20.2919 22.5125 19.3918 22.6335C18.5248 22.75 17.4225 22.75 16.0549 22.75H7.94513C6.57754 22.75 5.47522 22.75 4.60825 22.6335C3.70814 22.5125 2.95027 22.2536 2.34835 21.6516C1.74643 21.0497 1.48754 20.2919 1.36652 19.3918C1.24996 18.5248 1.24998 17.4225 1.25 16.0549V15.9451C1.24998 14.5775 1.24996 13.4752 1.36652 12.6082C1.48754 11.7081 1.74643 10.9503 2.34835 10.3483C2.95027 9.74643 3.70814 9.48754 4.60825 9.36652C4.80938 9.33948 5.02317 9.31872 5.25 9.30277ZM6.75 8C6.75 5.10051 9.10051 2.75 12 2.75C14.8995 2.75 17.25 5.10051 17.25 8V9.25344C16.8765 9.24999 16.4784 9.24999 16.0549 9.25H7.94513C7.52161 9.24999 7.12353 9.24999 6.75 9.25344V8ZM3.40901 11.409C3.68577 11.1322 4.07435 10.9518 4.80812 10.8531C5.56347 10.7516 6.56459 10.75 8 10.75H16C17.4354 10.75 18.4365 10.7516 19.1919 10.8531C19.9257 10.9518 20.3142 11.1322 20.591 11.409C20.8678 11.6858 21.0482 12.0743 21.1469 12.8081C21.2484 13.5635 21.25 14.5646 21.25 16C21.25 17.4354 21.2484 18.4365 21.1469 19.1919C21.0482 19.9257 20.8678 20.3142 20.591 20.591C20.3142 20.8678 19.9257 21.0482 19.1919 21.1469C18.4365 21.2484 17.4354 21.25 16 21.25H8C6.56459 21.25 5.56347 21.2484 4.80812 21.1469C4.07435 21.0482 3.68577 20.8678 3.40901 20.591C3.13225 20.3142 2.9518 19.9257 2.85315 19.1919C2.75159 18.4365 2.75 17.4354 2.75 16C2.75 14.5646 2.75159 13.5635 2.85315 12.8081C2.9518 12.0743 3.13225 11.6858 3.40901 11.409Z" fill="#9ca3af"/></svg>
                    <input
                      type={showPassword ? "text" : "password"}
                      value={formData.password}
                      onChange={e => setFormData({ ...formData, password: e.target.value })}
                      className="register-input w-full pl-12 pr-20 py-3.5 rounded-xl bg-[#f6f6f6] border-0 focus:ring-2 focus:ring-purple-500/20 transition-all text-gray-900 placeholder-gray-400"
                      style={{ backgroundColor: '#f6f6f6', border: 'none' }}
                      required
                    />
                    <div className="absolute right-4 top-1/2 -translate-y-1/2 flex items-center gap-2">
                      <button
                        type="button"
                        onClick={() => setShowPassword(!showPassword)}
                        className="p-1 text-gray-400 hover:text-gray-600 transition-colors"
                      >
                        {showPassword ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
                      </button>
                    </div>
                  </div>
                </div>
              </div>

              <div className="flex items-center justify-between">
                <label className="flex items-center gap-2 cursor-pointer group">
                  <div className="relative w-5 h-5">
                    <input
                      type="checkbox"
                      checked={rememberMe}
                      onChange={(e) => {
                        const checked = e.target.checked;
                        setRememberMe(checked);
                        localStorage.setItem('rememberMe', checked.toString());
                      }}
                      className="peer sr-only"
                    />
                    <div className="h-5 w-5 rounded border-2 border-gray-300 peer-checked:bg-gradient-to-br peer-checked:from-purple-500 peer-checked:to-pink-500 peer-checked:border-transparent transition-all" />
                    <Check className="h-3 w-3 absolute top-1 left-1 opacity-0 peer-checked:opacity-100 transition-opacity" style={{ color: 'white' }} />
                  </div>
                  <span className="text-sm text-gray-600 group-hover:text-gray-900 transition-colors">
                    {t('public.login.remember_me')}
                  </span>
                </label>
                <Link
                  to="/forgot-password"
                  className="text-sm font-medium text-gray-600 hover:text-purple-600 transition-colors"
                >
                  {t('public.login.forgot_password')}
                </Link>
              </div>

              <button
                type="submit"
                className="w-full rounded-xl py-3.5 px-6 font-semibold text-white bg-black hover:bg-gray-900 transition-all duration-300 relative overflow-hidden group shadow-lg hover:shadow-xl disabled:opacity-50 disabled:cursor-not-allowed"
                disabled={loading}
                style={{ color: 'white' }}
              >
                <span className="relative z-10 flex items-center justify-center gap-2">
                  {loading ? (
                    <>
                      <Loader2 className="h-5 w-5 animate-spin" />
                      {t('public.login.connecting')}
                    </>
                  ) : (
                    <>
                      <LogIn className="h-5 w-5" style={{ color: 'white' }} />
                      <span style={{ color: 'white' }}>{t('public.login.login_button')}</span>
                    </>
                  )}
                </span>
                <div
                  className="absolute inset-0 opacity-0 group-hover:opacity-10 transition-opacity duration-300"
                  style={{
                    background: 'radial-gradient(circle at center, white 0%, transparent 70%)',
                  }}
                />
              </button>

              <div className="relative flex items-center gap-3 py-2">
                <div className="flex-grow h-px bg-gray-200"></div>
                <span className="text-sm text-gray-500 font-medium">{t('public.login.or_divider')}</span>
                <div className="flex-grow h-px bg-gray-200"></div>
              </div>

              <GoogleSecureLoginButton
                text={t('public.login.google_button_text')}
                onError={(error) => setToast({ message: error.message, type: 'error' })}
                className="bg-white border border-gray-200 text-gray-900 shadow-sm rounded-xl"
              />
            </form>

            <p className="text-center text-gray-600">
              {t('public.login.no_account')}{' '}
              <Link
                to="/register"
                className="font-semibold text-transparent bg-clip-text bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700 transition-all"
              >
                {t('public.login.create_account_link')}
              </Link>
            </p>
          </div>
        </div>

        {/* Right Panel - Decorative en thème blanc */}
        <div className="hidden lg:flex w-1/2 relative overflow-hidden items-center justify-center" style={{ backgroundColor: '#f6f6f6' }}>
          {/* Glow effect subtil */}
          <div 
            className="absolute inset-0 blur-3xl opacity-10 rounded-3xl pointer-events-none"
            style={{
              background: 'linear-gradient(135deg, rgba(139, 92, 246, 0.3), rgba(236, 72, 153, 0.3), rgba(251, 146, 60, 0.2))',
              transform: 'scale(0.95)',
              animation: 'pulse 4s ease-in-out infinite'
            }}
          />
          
          <div className="relative z-10 text-center px-12 max-w-lg">
            {/* Logo avec effet subtil */}
            <div className="mb-8 transform transition-transform hover:scale-105 duration-300">
              <img 
                src={logoSvg} 
                alt="FocusPips" 
                style={{ height: '120px', width: 'auto', margin: '0 auto', filter: 'drop-shadow(0 10px 30px rgba(139, 92, 246, 0.2))' }} 
                className="object-contain" 
              />
            </div>
            
            <h2 
              className="text-3xl sm:text-4xl font-bold mb-4 text-gray-900"
            >
              {t('public.login.right_panel_title_prefix')}{' '}
              <span
                className="gradient-text inline-block"
                style={{
                  backgroundImage: 'linear-gradient(120deg, var(--purple) 0%, var(--pink) 50%, var(--orange) 100%)', backgroundSize: '200% 200%', animation: 'gradientShift 4s ease infinite',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                  backgroundClip: 'text'
                }}
              >
                {t('public.login.right_panel_title_highlight')}
              </span>
            </h2>
            <p className="text-lg text-gray-600 leading-relaxed max-w-md mx-auto">
              {t('public.login.right_panel_subtitle')}
            </p>

            {/* Points de fonctionnalités */}
            <div className="mt-8 space-y-3">
              <div className="flex items-center gap-3 text-gray-700">
                <div className="w-2 h-2 rounded-full bg-gradient-to-r from-purple-400 to-pink-400"></div>
                <span className="text-sm">{t('public.login.feature1')}</span>
              </div>
              <div className="flex items-center gap-3 text-gray-700">
                <div className="w-2 h-2 rounded-full bg-gradient-to-r from-purple-400 to-pink-400"></div>
                <span className="text-sm">{t('public.login.feature2')}</span>
              </div>
              <div className="flex items-center gap-3 text-gray-700">
                <div className="w-2 h-2 rounded-full bg-gradient-to-r from-purple-400 to-pink-400"></div>
                <span className="text-sm">{t('public.login.feature3')}</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Styles pour animations et autofill */}
      <style>{`
        @keyframes float {
          0%, 100% { transform: translate(0, 0) scale(1); }
          33% { transform: translate(30px, -30px) scale(1.1); }
          66% { transform: translate(-20px, 20px) scale(0.9); }
        }
        @keyframes gradientShift {
          0%, 100% { background-position: 0% 50%; }
          50% { background-position: 100% 50%; }
        }
        @keyframes pulse {
          0%, 100% { opacity: 0.1; }
          50% { opacity: 0.15; }
        }
        /* Correction de l'autofill pour garder le fond blanc */
        input:-webkit-autofill,
        input:-webkit-autofill:hover,
        input:-webkit-autofill:focus,
        input:-webkit-autofill:active {
          -webkit-box-shadow: 0 0 0 30px white inset !important;
          -webkit-text-fill-color: #111827 !important;
          box-shadow: 0 0 0 30px white inset !important;
          background-color: white !important;
        }
      `}</style>

      {toast && (
        <Toast
          message={toast.message}
          type={toast.type}
          onClose={() => setToast(null)}
        />
      )}

      {pending && (
        <SecureChallengeModal
          pendingSessionId={pending.pendingSessionId}
          challenge={pending.challenge}
          resolveDevice={resolvePasswordDeviceChallenge}
          resolveTotp={resolvePasswordTotpChallenge}
          resendDeviceCode={resendPasswordDeviceCode}
          onSuccess={handleChallengeSuccess}
          onCancel={handleChallengeCancel}
        />
      )}
    </div>
  );
}
